import { createBrowserRouter, redirect, useNavigate } from 'react-router-dom';
import MapMaterialIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import DescriptionIcon from '@mui/icons-material/Description';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LoginScreen from './screens/LoginScreen';
import { db } from './instant';
import { CREATE_EVENT_GROUP_ROUTE, CREATE_EVENT_ROUTE, DEFAULT_ROUTE, EVENTS_LIST_ROUTE, LOGIN_ROUTE } from './constants';
import ErrorScreen from './screens/ErrorScreen';
import CreateScreen from './screens/CreateScreen';
import EventsListScreen from './screens/EventsListScreen';
import EventScreen from './screens/EventScreen';
import { ComponentType, ReactNode, useEffect } from 'react';
import EventMapContainer from './components/EventMapContainer';
import EventInventory from './components/EventInventory';
import EventNotes from './components/EventNotes';
import EventTodo from './components/EventTodo';
import EventData from './components/EventData';
import EventInfo from './components/EventInfo';
import EventUsers from './components/EventUsers';
import EventProperties from './components/EventProperties';
import EventGroupScreen from './screens/EventGroupScreen';
import ReportsScreen from './screens/ReportsScreen';
import ReportInfoScreen from './screens/ReportInfoScreen';
import CreateReportScreen from './screens/CreateReportScreen';
import SettingsScreen from './screens/SettingsScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import SignupScreen from './screens/SignupScreen';
import UsersScreen from './screens/UsersScreen';
import UserScreen from './screens/UserScreen';
import GlobalInventoryScreen from './screens/GlobalInventoryScreen';
import InfoScreen from './screens/InfoScreen';
import LogisticsScreen from './screens/LogisticsScreen';
import TripDetailScreen from './screens/TripDetailScreen';
import OperationDetailScreen from './screens/OperationDetailScreen';
import DocsScreen from './screens/DocsScreen';
import { userHasAppAccess } from './lib/userAppAccess';

export interface EventTabConfig {
    id: string;
    label: string;
    icon: ComponentType;
    component: ComponentType<{ eventId: string }>;
    privateOnly?: boolean;
}

export const tripTabsConfig: { id: string; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'supply-flow', label: 'Supply flow' },
    { id: 'operations', label: 'Operations' },
    { id: 'inventory', label: 'Inventory' },
];

export const eventTabsConfig: EventTabConfig[] = [
    {
        id: 'map',
        label: 'Map',
        icon: MapMaterialIcon,
        component: EventMapContainer,
    },
    {
        id: 'info',
        label: 'Info',
        icon: InfoIcon,
        component: EventInfo,
    },
    {
        id: 'inventory',
        label: 'Inventory',
        icon: ListIcon,
        component: EventInventory,
    },
    {
        id: 'todo',
        label: 'To-Do',
        icon: TaskAltIcon,
        component: EventTodo,
    },
    {
        id: 'notes',
        label: 'Notes',
        icon: DescriptionIcon,
        component: EventNotes,
    },
    {
        id: 'data',
        label: 'Data',
        icon: BarChartIcon,
        component: EventData,
    },
    {
        id: 'eventUsers',
        label: 'Users',
        icon: PeopleIcon,
        component: EventUsers,
        privateOnly: true,
    },
    {
        id: 'settings',
        label: 'Properties',
        icon: SettingsIcon,
        component: EventProperties,
        privateOnly: true,
    },
];

/** Event tab IDs shown in the bottom bar (subset of `eventTabsConfig`). */
export const EVENT_BOTTOM_TAB_IDS = ['map', 'inventory', 'todo', 'data', 'notes'] as const;

export interface GlobalTabConfig {
    id: string;
    label: string;
    icon: ComponentType;
    route: string;
    /** When set, use a plain link to this URL (full navigation) instead of React Router. */
    externalHref?: string;
}

export const globalTabsConfig: GlobalTabConfig[] = [
    {
        id: 'list',
        label: 'Events',
        icon: EventIcon,
        route: '/events',
    },
    {
        id: 'inventory',
        label: 'Inventory',
        icon: ListIcon,
        route: '/inventory/items',
    },
    {
        id: 'logistics',
        label: 'Logistics',
        icon: LocalShippingIcon,
        route: '/logistics/operations',
    },
    {
        id: 'users',
        label: 'Users',
        icon: PeopleIcon,
        route: '/users',
    },
    {
        id: 'info',
        label: 'Info',
        icon: InfoIcon,
        route: '/info',
    },
    {
        id: 'notifications',
        label: 'Notifications',
        icon: NotificationsIcon,
        route: '/notifications',
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        route: '/settings',
    },
    {
        id: 'docs',
        label: 'Docs',
        icon: MenuBookIcon,
        route: '/docs/',
        externalHref: '/docs/',
    },
];

interface RouteProps {
    children: ReactNode;
}

interface RouteConfig {
    path: string;
    element?: ReactNode;
    private?: boolean;
    loader?: (...args: any[]) => any;
    errorElement?: ReactNode;
}

interface RouteMode {
    prefix: string;
    isPrivate: boolean;
    viewOnly: boolean;
}

function PrivateRoute({ children }: RouteProps) {
    const { isLoading: authLoading, user, error: authError } = db.useAuth();
    const navigate = useNavigate();

    // Query current user data
    const {
        data: userData,
        isLoading: userLoading,
        error: userError,
    } = db.useQuery({
        $users: {
            $: { where: { email: user?.email || '' } },
        },
    });

    // Redirect to login if not authenticated (must be in useEffect to avoid setState during render)
    useEffect(() => {
        if (!authLoading && !authError && !user) {
            navigate(LOGIN_ROUTE, { replace: true });
        }
    }, [authLoading, authError, user, navigate]);

    if (authLoading || userLoading) {
        return <div>Loading...</div>;
    }

    if (authError) {
        return <div>Uh oh! {authError.message}</div>;
    }

    if (userError) {
        return <div>Uh oh! {userError.message}</div>;
    }

    if (!user) {
        // Return null while redirect is pending
        return null;
    }

    const currentUser = userData.$users?.[0];
    const hasAppAccess = userHasAppAccess(currentUser);

    if (!hasAppAccess) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    padding: '20px',
                    textAlign: 'center',
                }}
            >
                <h2 style={{ color: '#333', marginBottom: '10px' }}>Unauthorized</h2>
                <p style={{ color: '#666', marginBottom: '20px' }}>You don&apos;t have permission to access this application.</p>
                <p style={{ color: '#666' }}>Please contact your administrator for access.</p>
                <button
                    onClick={() => db.auth.signOut()}
                    style={{
                        marginTop: '20px',
                        padding: '12px 24px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '16px',
                    }}
                >
                    Sign Out
                </button>
            </div>
        );
    }

    return <>{children}</>;
}

function privateRoute(children: ReactNode) {
    return <PrivateRoute>{children}</PrivateRoute>;
}

// Utility to create redirect loaders for event routes
const createEventRedirectLoader = (prefix: string) => {
    return (args: { params: { eventId: string } }) => {
        const { eventId } = args.params;
        return redirect(`${prefix}/${eventId}/map`);
    };
};

// Route modes (normal and viewOnly)
const routeModes: RouteMode[] = [
    { prefix: '/event', isPrivate: true, viewOnly: false },
    { prefix: '/viewevent', isPrivate: false, viewOnly: true },
];

// Generate routes
const generateRoutes = (): RouteConfig[] => {
    // Base routes
    const routes: RouteConfig[] = [
        {
            path: '/',
            loader: () => redirect(DEFAULT_ROUTE),
            errorElement: <ErrorScreen />,
        },
        {
            path: LOGIN_ROUTE,
            element: <LoginScreen />,
        },
        {
            path: CREATE_EVENT_ROUTE,
            element: <CreateScreen />,
            private: true,
        },
        {
            path: CREATE_EVENT_GROUP_ROUTE,
            element: <CreateScreen />,
            private: true,
        },
        {
            path: EVENTS_LIST_ROUTE,
            element: <EventsListScreen />,
            private: true,
        },
        {
            path: '/group/:groupId',
            element: <EventGroupScreen />,
            private: true,
        },
        {
            path: '/event/:eventId/reports',
            element: <ReportsScreen />,
            private: true,
        },
        {
            path: '/event/:eventId/create-report',
            element: <CreateReportScreen />,
            private: true,
        },
        {
            path: '/report/:reportId',
            element: <ReportInfoScreen />,
            private: true,
        },
        {
            path: '/settings',
            element: <SettingsScreen />,
            private: true,
        },
        {
            path: '/notifications',
            element: <NotificationsScreen />,
            private: true,
        },
        {
            path: '/users',
            element: <UsersScreen />,
            private: true,
        },
        {
            path: '/user/:userId',
            element: <UserScreen />,
            private: true,
        },
        {
            path: '/inventory/items',
            element: <GlobalInventoryScreen />,
            private: true,
        },
        {
            path: '/inventory/categories',
            element: <GlobalInventoryScreen />,
            private: true,
        },
        {
            path: '/inventory/storage',
            element: <GlobalInventoryScreen />,
            private: true,
        },
        {
            path: '/inventory/events',
            element: <GlobalInventoryScreen />,
            private: true,
        },
        {
            path: '/inventory',
            loader: () => redirect('/inventory/items'),
            private: true,
        },
        {
            path: '/logistics/operations',
            element: <LogisticsScreen />,
            private: true,
        },
        {
            path: '/logistics/trips',
            element: <LogisticsScreen />,
            private: true,
        },
        {
            path: '/logistics',
            loader: () => redirect('/logistics/operations'),
            private: true,
        },
        {
            path: '/logistics/trip/:tripId',
            loader: ({ params }: { params: { tripId: string } }) => redirect(`/logistics/trip/${params.tripId}/overview`),
            private: true,
        },
        {
            path: '/logistics/trip/:tripId/:tab',
            element: <TripDetailScreen />,
            private: true,
        },
        {
            path: '/logistics/operation/:operationId/tasks',
            element: <OperationDetailScreen />,
            private: true,
        },
        {
            path: '/logistics/operation/:operationId/confirmation',
            element: <OperationDetailScreen />,
            private: true,
        },
        {
            path: '/logistics/operation/:operationId/supply',
            element: <OperationDetailScreen />,
            private: true,
        },
        {
            path: '/logistics/operation/:operationId/inventory',
            element: <OperationDetailScreen />,
            private: true,
        },
        {
            path: '/logistics/operation/:operationId',
            loader: ({ params }: { params: { operationId: string } }) => redirect(`/logistics/operation/${params.operationId}/tasks`),
            private: true,
        },
        {
            path: '/logistics/active-events',
            loader: () => redirect('/inventory/events'),
            private: true,
        },
        {
            path: '/info',
            element: <InfoScreen />,
            private: true,
        },
        {
            path: '/signup',
            element: <SignupScreen />,
        },
        // /docs is served as static files in production; in dev we show a placeholder so we don't redirect to /events
        {
            path: '/docs',
            element: <DocsScreen />,
        },
        {
            path: '/docs/*',
            element: <DocsScreen />,
        },
    ];

    // Generate all event routes for both modes
    routeModes.forEach(({ prefix, isPrivate, viewOnly }) => {
        // Reuse the same redirect loader for both root and wildcard routes
        const redirectLoader = createEventRedirectLoader(prefix);

        // Add tab routes
        eventTabsConfig.forEach(({ id: tab, privateOnly }) => {
            // Skip view-only routes for tabs that should be private only
            if (viewOnly && privateOnly) return;

            routes.push({
                path: `${prefix}/:eventId/${tab}`,
                element: isPrivate ? privateRoute(<EventScreen viewOnly={viewOnly} />) : <EventScreen viewOnly={viewOnly} />,
                private: isPrivate,
            });
        });

        // Add redirect routes - reusing the same loader function
        routes.push({
            path: `${prefix}/:eventId`,
            loader: redirectLoader,
        });

        routes.push({
            path: `${prefix}/:eventId/*`,
            loader: redirectLoader,
        });
    });

    return routes;
};

// Apply permissions and create the router
const createRouter = () => {
    const routes = generateRoutes();

    return createBrowserRouter(
        routes.map(route => {
            const element = route.element != null ? (route.private ? privateRoute(route.element) : route.element) : undefined;
            return {
                path: route.path,
                ...(element !== undefined && { element }),
                loader: route.loader,
                errorElement: route.errorElement,
            };
        })
    );
};

/** Lazy router factory — avoid createBrowserRouter side effects when importing tab config. */
export function createAppRouter() {
    return createRouter();
}
